import "./DealAssociations.css";

import { useMemo } from "react";

import { TrashDark } from "../../../../assets";
import { Autocomplete, Button, Column, Divider, ErrorBox, FullPageCard, Row, Select } from "../../../../widget";
import { AddEditCompanyModal } from "../../operations/companies/panels/AddEditCompanyModal";
import { CreateProjectPanel, NextPreviousPageButtons } from "../components";
import { useDealAssociations } from "./useDealAssociations";

export const DealAssociations = (): JSX.Element => {
  const {
    errors,
    investingIntoSelectData,
    companySelectData,
    projectsSelectData,
    selectedInvestedInto,
    associations,
    companyDraft,
    projectDrafts,
    landholdingDrafts,
    showAddEditCompanyModal,
    showProjectCreatePanel,
    openAddEditCompanyModal,
    closeAddEditCompanyModal,
    openProjectCreatePanel,
    closeProjectCreatePanel,
    saveCompanyDraft,
    clearCompanyDraft,
    saveProjects,
    removeLandholdingDraft,
    removeProjectDraft,
    onInvestedIntoSelect,
    onCompanySelect,
    onProjectSelect,
    onCompanyTypeSearch,
    onProjectTypeSearch,
    removeAssociation,
  } = useDealAssociations();

  const associationRows = useMemo(() => associations, [associations]);

  return (
    <div className="DealAssociationsContainer">
      <FullPageCard corners="rounded" padding="ll">
        <div className="DealAssociationsCard">
          <div className="DealAssociationsContent">
            <Row spacingV="l">
              <Column span={12}>
                <h2>Associations</h2>
              </Column>
            </Row>
            {errors && errors.length > 0 && (
              <Row spacingV="ml">
                <Column span={10}>
                  <ErrorBox text={errors.map((error) => error.message)} />
                </Column>
              </Row>
            )}
            <Row spacingV="xl">
              <Column span={12}>
                <p className="body1">
                  Associations link this deal to what you&apos;re investing in, such as a company, project, or
                  landholding. Use the drop-downs to search and select existing records, or create a new one if it
                  doesn&apos;t exist. There can only be one associated company per deal.
                </p>
              </Column>
            </Row>
            <Row spacingV="ll">
              <div className="DealAssociationsColumn">
                <Select
                  label="What are you investing into?"
                  placeholder="Select"
                  data={investingIntoSelectData}
                  value={selectedInvestedInto}
                  onChange={onInvestedIntoSelect}
                />
              </div>
            </Row>
            <Row spacingV="ll">
              <Column span={12}>
                <Divider type="horizontal" />
              </Column>
            </Row>
            {(selectedInvestedInto === "Company" || selectedInvestedInto === "Company + Project(s)") && (
              <>
                <Row spacingV="ll">
                  <div className="DealAssociationsColumn">
                    <Autocomplete
                      label="Company name"
                      placeholder="Search"
                      data={companySelectData}
                      onSelect={onCompanySelect}
                      onTypeSearch={onCompanyTypeSearch}
                      disabled={!!companyDraft || associationRows.some((x) => x.objectType === "Collaborator")}
                    />
                  </div>
                  <Column span={2}>
                    <Button
                      text="Add"
                      className="DealAssociationsAddButton"
                      onClick={openAddEditCompanyModal}
                      hasNoPadding
                      disabled={!!companyDraft || associationRows.some((x) => x.objectType === "Collaborator")}
                    />
                  </Column>
                </Row>
                <Row spacingV="ll">
                  <Column span={12}>
                    <Divider type="horizontal" />
                  </Column>
                </Row>
              </>
            )}
            {(selectedInvestedInto === "Project(s)" || selectedInvestedInto === "Company + Project(s)") && (
              <>
                <Row spacingV="ll">
                  <div className="DealAssociationsColumn">
                    <Autocomplete
                      label="Project or landholding name"
                      placeholder="Search"
                      data={projectsSelectData}
                      onSelect={onProjectSelect}
                      onTypeSearch={onProjectTypeSearch}
                      clearOnSelect
                    />
                  </div>
                  <Column span={2}>
                    <Button
                      text="Add"
                      className="DealAssociationsAddButton"
                      onClick={openProjectCreatePanel}
                      hasNoPadding
                    />
                  </Column>
                </Row>
                <Row spacingV="ll">
                  <Column span={12}>
                    <Divider type="horizontal" />
                  </Column>
                </Row>
              </>
            )}
            {selectedInvestedInto !== null && (
              <>
                <Row spacingV="ml">
                  <Column span={12}>
                    <p className="body1">
                      <b>Associations list</b>
                    </p>
                  </Column>
                </Row>
                <Row spacingV="xl">
                  <div className="DealAssociationsList">
                    <div className="DealAssociationsListHeader">
                      <p className="body3">
                        <b>Opportunity</b>
                      </p>
                      <p className="body3">
                        <b>Remove</b>
                      </p>
                    </div>
                    {companyDraft && (
                      <div className="DealAssociationsListRow" key="pending-company-draft">
                        <p className="body1">{companyDraft.name}</p>
                        <Button
                          text=""
                          variant="secondary"
                          icon={<TrashDark className="DealAssociationsRemoveIcon" />}
                          hasNoPadding
                          minWidth={false}
                          onClick={clearCompanyDraft}
                        />
                      </div>
                    )}
                    {projectDrafts
                      .filter((draft) => !draft.landholdingName)
                      .map((draft) => (
                        <div className="DealAssociationsListRow" key={draft.key}>
                          <p className="body1">{draft.name} | Project</p>
                          <Button
                            text=""
                            variant="secondary"
                            icon={<TrashDark className="DealAssociationsRemoveIcon" />}
                            hasNoPadding
                            minWidth={false}
                            onClick={() => removeProjectDraft(draft.key)}
                          />
                        </div>
                      ))}
                    {landholdingDrafts.map((draft) => (
                      <div className="DealAssociationsListRow" key={draft.key}>
                        <p className="body1">{draft.name} | Landholding</p>
                        <Button
                          text=""
                          variant="secondary"
                          icon={<TrashDark className="DealAssociationsRemoveIcon" />}
                          hasNoPadding
                          minWidth={false}
                          onClick={() => removeLandholdingDraft(draft.key)}
                        />
                      </div>
                    ))}
                    {associationRows.map((value) => (
                      <div className="DealAssociationsListRow" key={`${value.objectUuid}-${value.name}`}>
                        <p className="body1">{value.name}</p>
                        <Button
                          text=""
                          variant="secondary"
                          icon={<TrashDark className="DealAssociationsRemoveIcon" />}
                          hasNoPadding
                          minWidth={false}
                          onClick={() => removeAssociation(value)}
                        />
                      </div>
                    ))}
                  </div>
                </Row>
              </>
            )}
            <Row className="DealAssociationsFooter">
              <Column span={12}>
                <NextPreviousPageButtons />
              </Column>
            </Row>
          </div>
        </div>
      </FullPageCard>
      <AddEditCompanyModal
        show={showAddEditCompanyModal}
        onClose={closeAddEditCompanyModal}
        submitMode="deferred"
        onDeferredSave={saveCompanyDraft}
      />
      <CreateProjectPanel show={showProjectCreatePanel} onClose={closeProjectCreatePanel} onSave={saveProjects} />
    </div>
  );
};
